// AnimalAddendumSendPage — /send/animal-addendum: a current tenant is adding an
// animal, so collect the animal's details from the household and then send the
// Animal Addendum for signature (WEL-153, docs/animal-addendum.md).
//
// PROPERTY → UNIT first. There is no application behind this — the tenancy
// already exists — so this page is the MANUAL branch of LeaseholderChangePage
// with a different payload: pick the property and unit, and everything below is
// loaded from the unit's current occupancy.
//
// What staff confirm BEFORE anything goes out (Andrea, 2026-09-11): the four
// money fields and the rent start date. There is no review step afterwards —
// the first household member to complete the form sends the addendum straight
// out for signature — so this page is the only place the numbers are checked.
//
// Send-page only: an applicant group's animals ride on the lease itself, so
// there is deliberately no household "Send…" context entry for this document
// (the Send-hub tile carries no contextTo/contextNote).
//
// Ids never reach the screen: the occupancy id addresses every call and is
// never rendered (docs/no-ids-in-ui.md).

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SearchSelect } from '@/components/SearchSelect';
import { MoneyInput } from '@/components/MoneyInput';
import { AccommodationAnimalsBlock } from '@/components/AccommodationAnimalsBlock';
import { toast } from '@/components/Toast';
import { useAuth } from '@/contexts/AuthContext';
import {
  listProperties,
  listUnits,
  type PropertyOption,
  type UnitOption,
  unitOptionLabel,
} from '@/hooks/useLeaseLifecycle';
import {
  AnimalAddendumNotEnabledError,
  AnimalAddendumPriorSendOpenError,
  animalAddendumErrorMessage,
  cancelAnimalAddendum,
  getAnimalAddendumContext,
  initiateAnimalAddendum,
  resendAnimalAddendumIntake,
  type AnimalAddendumContext,
  type AnimalAddendumInitiateRecipient,
  type AnimalAddendumPriorSend,
  type AnimalAddendumRecipientKind,
} from '@/lib/animalAddendumClient';
import {
  accommodationReasons,
  mayMarkAccommodationAnimals,
  accommodationSummaryLines,
  addAccommodationRow,
  buildAccommodationAnimals,
  removeAccommodationRow,
  updateAccommodationRow,
  type AccommodationRow,
} from '@/lib/animalAddendumAccommodation';
import { formatMoneyCents } from '@/lib/money';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** One signer row, as this page edits them — the same first / last / email row
 *  every other tenancy send page uses (WEL-161). */
interface RecipientRow {
  /** Stable row key; the AppFolio tenant id when the row came from AppFolio. */
  key: string;
  tenantId?: string;
  firstName: string;
  lastName: string;
  email: string;
  kind: AnimalAddendumRecipientKind;
}

let rowSeq = 0;
function blankRow(): RecipientRow {
  rowSeq += 1;
  return { key: `manual-${rowSeq}`, firstName: '', lastName: '', email: '', kind: 'resident' };
}

/** AppFolio gives first/last; an older server gives one name — split on the last space. */
function splitName(p: { name: string; firstName?: string; lastName?: string }): {
  firstName: string;
  lastName: string;
} {
  if (p.firstName !== undefined || p.lastName !== undefined) {
    return { firstName: (p.firstName ?? '').trim(), lastName: (p.lastName ?? '').trim() };
  }
  const parts = p.name.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { firstName: parts[0] ?? '', lastName: '' };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1]! };
}

function rowName(r: RecipientRow): string {
  return `${r.firstName.trim()} ${r.lastName.trim()}`.trim();
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso.includes('T') ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/**
 * The one-line state of a prior round, and whether it still blocks a new send.
 *
 * `open` comes from the SERVER's `agreementOpen`, never from reading
 * `agreementStatus`: finished means executed OR declined OR cancelled, and a
 * client that only recognised "Fully Executed" would treat a declined round as
 * live forever — the page would offer Cancel and Resend on something that can
 * no longer be either. When the field is missing entirely (an older API), the
 * fallback errs toward "still open", which costs a refused send the operator
 * can recover from instead of a second addendum nobody meant to send.
 */
function priorSendState(p: AnimalAddendumPriorSend): {
  headline: string;
  tone: 'neutral' | 'info' | 'success' | 'warning';
  /** Something is still live: offer Cancel / Resend form, refuse a new send. */
  open: boolean;
  /** Finished and signed: a second addendum is allowed, behind a confirmation. */
  executed: boolean;
  /** When it finished, for the confirmation copy. */
  executedAt: string | null;
} {
  const open = p.agreementOpen ?? (!p.cancelledAt && !p.agreementExecutedAt);
  const executedAt = p.agreementExecutedAt ?? null;
  const executed = !open && executedAt !== null;

  if (p.cancelledAt) {
    return {
      headline: `Cancelled ${fmtDate(p.cancelledAt)}`.trim(),
      tone: 'neutral',
      open,
      executed: false,
      executedAt: null,
    };
  }
  if (!open) {
    // Finished. Executed says so with its date; anything else finished (a
    // decline) is named by the server's own status rather than guessed at.
    const when = fmtDate(executedAt);
    return {
      headline: executedAt
        ? when
          ? `Fully executed ${when}`
          : 'Fully executed'
        : (p.agreementStatus ?? 'No longer active'),
      tone: executedAt ? 'success' : 'neutral',
      open,
      executed,
      executedAt,
    };
  }
  if (p.status === 'sent') {
    return {
      headline: p.agreementStatus ?? 'Agreement out for signature',
      tone: 'info',
      open,
      executed: false,
      executedAt: null,
    };
  }
  if (p.status === 'locked') {
    return {
      headline: 'Details received — the addendum is going out for signature',
      tone: 'info',
      open,
      executed: false,
      executedAt: null,
    };
  }
  const when = fmtDate(p.initiatedAt);
  return {
    headline: when
      ? `Form sent ${when} · waiting on the household`
      : 'Form sent · waiting on the household',
    tone: 'warning',
    open,
    executed: false,
    executedAt: null,
  };
}

export function AnimalAddendumSendPage(): React.JSX.Element {
  // WEL-182: "RRA or pending RRA" is for logins on the RRA staff roster (the API refuses anyone else).
  const { capabilities } = useAuth();
  const mayMarkAccommodation = mayMarkAccommodationAnimals(capabilities);
  // --- Property / unit picker -------------------------------------------
  const [properties, setProperties] = useState<PropertyOption[]>([]);
  /** One-shot guard for the property load. A REF on purpose — WEL-129: as state
   *  it would re-run its own effect, whose cleanup then cancels the in-flight
   *  fetch and leaves the picker on "Loading…" forever. */
  const propsRequestedRef = useRef(false);
  const [propsLoading, setPropsLoading] = useState(true);
  const [propsError, setPropsError] = useState<string | null>(null);
  const [propertyId, setPropertyId] = useState('');
  const [units, setUnits] = useState<UnitOption[]>([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unitsError, setUnitsError] = useState<string | null>(null);
  const [unitId, setUnitId] = useState('');

  // --- The tenancy context ----------------------------------------------
  const [ctx, setCtx] = useState<AnimalAddendumContext | null>(null);
  const [ctxLoading, setCtxLoading] = useState(false);
  const [ctxError, setCtxError] = useState<string | null>(null);

  // --- The working set ---------------------------------------------------
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [currentRentCents, setCurrentRentCents] = useState(0);
  const [currentDepositCents, setCurrentDepositCents] = useState(0);
  const [rentPerAnimalCents, setRentPerAnimalCents] = useState(5000);
  const [depositPerAnimalCents, setDepositPerAnimalCents] = useState(50000);
  const [rentStartDate, setRentStartDate] = useState('');
  /** WEL-182: the animals staff mark "RRA or pending RRA" — $0 on the household's form and the addendum. */
  const [accommodationRows, setAccommodationRows] = useState<AccommodationRow[]>([]);

  const [busy, setBusy] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ names: string[] } | null>(null);
  const [actionBusy, setActionBusy] = useState<'cancel' | 'resend' | 'refresh' | null>(null);
  /** Open when a finished addendum exists and staff pressed Send anyway. The
   *  server no longer refuses that case, so this dialog is the ONLY thing
   *  between a slip of the hand and a second addendum the household did not
   *  ask for. */
  const [confirmSecond, setConfirmSecond] = useState(false);

  // Load the property list once, on mount.
  useEffect(() => {
    if (propsRequestedRef.current) return;
    propsRequestedRef.current = true;
    let cancelled = false;
    setPropsLoading(true);
    listProperties()
      .then((list) => {
        if (!cancelled) setProperties(list);
      })
      .catch((err) => {
        if (!cancelled) {
          setPropsError(err instanceof Error ? err.message : 'Failed to load properties');
        }
      })
      .finally(() => {
        if (!cancelled) setPropsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Units follow the property.
  useEffect(() => {
    if (!propertyId) {
      setUnits([]);
      setUnitId('');
      setUnitsError(null);
      return;
    }
    let cancelled = false;
    setUnitsLoading(true);
    setUnitsError(null);
    listUnits(propertyId)
      .then((list) => {
        if (cancelled) return;
        setUnits(list);
        setUnitId((prev) => (list.some((u) => u.unitId === prev) ? prev : (list[0]?.unitId ?? '')));
      })
      .catch((err) => {
        if (!cancelled) setUnitsError(err instanceof Error ? err.message : 'Failed to load units');
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  /** Load (or reload) the tenancy for the picked unit and refill the working set.
   *  `signal` lets a unit change abandon an in-flight load without clobbering the
   *  newer one's state. */
  const loadContext = useCallback(
    async (pId: string, uId: string, signal?: { cancelled: boolean }) => {
      setCtxLoading(true);
      setCtxError(null);
      try {
        const next = await getAnimalAddendumContext(pId, uId);
        if (signal?.cancelled) return;
        setCtx(next);
        setRecipients([
          ...next.residents.map((r) => ({
            key: r.tenantId || `resident-${r.name}`,
            ...(r.tenantId ? { tenantId: r.tenantId } : {}),
            ...splitName(r),
            email: r.email ?? '',
            kind: 'resident' as const,
          })),
          ...next.cosigners.map((c) => ({
            key: c.tenantId ? `cosigner:${c.tenantId}` : `cosigner-${c.name}`,
            ...(c.tenantId ? { tenantId: c.tenantId } : {}),
            ...splitName(c),
            email: c.email ?? '',
            kind: 'cosigner' as const,
          })),
        ]);
        setRentPerAnimalCents(next.defaults.rentPerNewAnimalCents);
        setDepositPerAnimalCents(next.defaults.depositPerNewAnimalCents);
        setRentStartDate(next.defaults.rentStartDate);
        // WEL-160: prefilled from the animals on file at the standard rate —
        // an estimate staff confirm, never a blank $0 when an animal lives there.
        setCurrentRentCents(next.defaults.currentRentCents);
        setCurrentDepositCents(next.defaults.currentDepositCents);
      } catch (err) {
        if (signal?.cancelled) return;
        setCtx(null);
        setCtxError(animalAddendumErrorMessage(err, 'Could not load the tenancy for this unit'));
      } finally {
        if (!signal?.cancelled) setCtxLoading(false);
      }
    },
    [],
  );

  // On unit pick, load the tenancy. Everything below the picker is unit-scoped,
  // so a unit change resets it all.
  useEffect(() => {
    setCtx(null);
    setCtxError(null);
    setRecipients([]);
    setAccommodationRows([]);
    setSendError(null);
    setSent(null);
    if (!propertyId || !unitId) return;
    const signal = { cancelled: false };
    void loadContext(propertyId, unitId, signal);
    return () => {
      signal.cancelled = true;
    };
  }, [propertyId, unitId, loadContext]);

  const included = recipients;
  const includedResidents = included.filter((r) => r.kind === 'resident');
  const prior = ctx?.priorSend ? priorSendState(ctx.priorSend) : null;
  const notEnabled = ctx?.notEnabled === true || ctx?.sendEnabled === false;

  // What a pending "RRA or pending RRA" row defaults its if-not-approved amounts to.
  const accommodationRates = { rentPerAnimalCents, depositPerAnimalCents };
  // Off the roster the block is not shown, so nothing in it counts (it can only be empty then anyway).
  const effectiveAccommodationRows = mayMarkAccommodation ? accommodationRows : [];
  const hasAccommodation = effectiveAccommodationRows.length > 0;
  const accommodationSummary = accommodationSummaryLines(effectiveAccommodationRows, accommodationRates);

  // Validation — each reason renders next to the (disabled) Send button.
  const reasons: string[] = [];
  if (ctx) {
    if (!ctx.occupancyId) {
      reasons.push('AppFolio shows no current tenancy for this unit — there is nobody to send to.');
    }
    if (includedResidents.length === 0) {
      reasons.push('At least one resident has to receive the form.');
    }
    included.forEach((r, idx) => {
      const who = rowName(r) || `Signer ${idx + 1}`;
      if (!rowName(r)) reasons.push(`Signer ${idx + 1} needs a name.`);
      if (!EMAIL_RE.test(r.email.trim())) {
        reasons.push(`${who} needs a valid email, or take them off the send.`);
      }
    });
    if (rentPerAnimalCents <= 0) {
      reasons.push('Rent per new animal has to be more than $0.');
    }
    reasons.push(...accommodationReasons(effectiveAccommodationRows, accommodationRates));
    if (!rentStartDate) reasons.push('Set the date the animal rent starts.');
    if (prior?.open) {
      reasons.push('A form or agreement is already out — cancel it or resend the form instead.');
    }
  }
  const canSend = !busy && ctx !== null && !notEnabled && reasons.length === 0;

  const addAccommodation = () => setAccommodationRows((prev) => addAccommodationRow(prev));
  const updateAccommodation = (key: string, patch: Partial<Omit<AccommodationRow, 'key'>>) =>
    setAccommodationRows((prev) => updateAccommodationRow(prev, key, patch));
  const removeAccommodation = (key: string) =>
    setAccommodationRows((prev) => removeAccommodationRow(prev, key));

  const updateRecipient = (key: string, patch: Partial<RecipientRow>) =>
    setRecipients((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const removeRecipient = (key: string) =>
    setRecipients((prev) => prev.filter((r) => r.key !== key));

  /** WEL-160: where the prefilled "currently charged / held" number came from —
   *  read from AppFolio, cited under the field so staff confirm a fact. */
  const chargeHelper = (which: 'rent' | 'deposit'): string => {
    const c = ctx?.currentCharges;
    if (!c) return '';
    if (which === 'rent') {
      const extra =
        c.nonMonthlyAnimalLines.length > 0
          ? ` AppFolio also shows ${c.nonMonthlyAnimalLines
              .map((l) => `${l.description} ${l.amountDisplay}${l.frequency ? ` (${l.frequency.toLowerCase()})` : ''}`)
              .join(', ')} — not monthly, so not counted here.`
          : '';
      switch (c.rentSource) {
        case 'appfolio':
          return (
            `From AppFolio: ${c.animalRentLines
              .map((l) => `${l.description} ${l.amountDisplay}/month`)
              .join(' + ')} (active recurring charge).` + extra
          );
        case 'none':
          return 'AppFolio shows no animal rent line on this tenancy — $0 unless the ledger says otherwise.' + extra;
        case 'unknown':
          return 'AppFolio could not say — enter what the ledger shows.';
        default:
          return "Couldn't read AppFolio's charges just now — enter what the ledger shows.";
      }
    }
    const held = c.depositHeldTotalDisplay ? ` AppFolio holds ${c.depositHeldTotalDisplay} in deposits for this tenancy in total.` : '';
    switch (c.depositSource) {
      case 'appfolio':
        return `From AppFolio: animal deposit charges on this tenancy.${held}`;
      case 'none':
        return c.animalDepositNegative
          ? `AppFolio shows an animal deposit refund with no matching charge — check the ledger before sending.${held}`
          : `AppFolio shows no animal deposit charge on this tenancy — $0 unless the ledger says otherwise.${held}`;
      case 'unknown':
        return `AppFolio knows the total held but not the animal share yet — enter what the ledger shows.${held}`;
      default:
        return "Couldn't read AppFolio's deposits just now — enter what the ledger shows.";
    }
  };

  const reload = async () => {
    if (propertyId && unitId) await loadContext(propertyId, unitId);
  };

  /** The Send button. A finished prior round asks first; everything else goes. */
  const handleSendClick = () => {
    if (!canSend) return;
    if (prior?.executed) {
      setConfirmSecond(true);
      return;
    }
    void handleSend();
  };

  const handleSend = async () => {
    if (!canSend || !ctx?.occupancyId) return;
    setConfirmSecond(false);
    setBusy(true);
    setSendError(null);
    const body: AnimalAddendumInitiateRecipient[] = included.map((r) => ({
      ...(r.tenantId ? { tenantId: r.tenantId } : {}),
      name: rowName(r),
      email: r.email.trim(),
      kind: r.kind,
    }));
    // The block is valid here (canSend), so this is never null; an empty block sends nothing.
    const accommodationAnimals =
      buildAccommodationAnimals(effectiveAccommodationRows, accommodationRates) ?? [];
    try {
      const res = await initiateAnimalAddendum(ctx.occupancyId, {
        propertyId,
        unitId,
        recipients: body,
        fees: {
          currentRentCents,
          currentDepositCents,
          rentPerNewAnimalCents: rentPerAnimalCents,
          depositPerNewAnimalCents: depositPerAnimalCents,
        },
        rentStartDate,
        ...(accommodationAnimals.length > 0 ? { accommodationAnimals } : {}),
        // A prior round that is finished is not an accident — say so explicitly
        // rather than letting the server guess at a duplicate.
        ...(prior?.executed ? { confirmNewSend: true } : {}),
      });
      const names = body.map((r) => r.name).filter(Boolean);
      setSent({ names });
      toast.success(
        names.length > 0
          ? `Animal form sent to ${names.join(', ')}.`
          : `Animal form sent — ${res.recipientsEmailed} ${res.recipientsEmailed === 1 ? 'link' : 'links'} emailed.`,
        { title: 'Animal addendum' },
      );
    } catch (err) {
      if (err instanceof AnimalAddendumPriorSendOpenError) {
        // Two situations, and the reload is what makes the recovery work in
        // both. `priorFullyExecuted` means the round finished while this page
        // was open — no client guard could have fired on state we did not have
        // — so the reload flips `prior.executed` and the next Send opens the
        // confirmation instead of failing again. Otherwise something is still
        // live, and the refreshed card shows what.
        setSendError(
          err.priorFullyExecuted
            ? 'That addendum finished signing while this page was open. Press Send again and confirm to start a second one.'
            : 'A form or agreement is already out for this tenancy. Cancel it, or resend the form, before sending a new one.',
        );
        await reload();
      } else if (err instanceof AnimalAddendumNotEnabledError) {
        setSendError(err.message);
      } else {
        setSendError(animalAddendumErrorMessage(err, 'Could not send the animal form'));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleCancel = async () => {
    if (!ctx?.occupancyId) return;
    setActionBusy('cancel');
    setSendError(null);
    try {
      const res = await cancelAnimalAddendum(ctx.occupancyId);
      toast.success(
        res.alreadyCancelled
          ? 'This animal addendum was already cancelled.'
          : `Animal addendum cancelled.${
              res.voidedSubmissionId ? ' The agreement out for signing was voided.' : ''
            }${
              res.recipientsEmailed > 0
                ? ` ${res.recipientsEmailed} ${res.recipientsEmailed === 1 ? 'person' : 'people'} notified.`
                : ''
            }`,
        { title: 'Animal addendum' },
      );
      await reload();
    } catch (err) {
      setSendError(animalAddendumErrorMessage(err, 'Could not cancel this animal addendum'));
    } finally {
      setActionBusy(null);
    }
  };

  const handleResend = async () => {
    if (!ctx?.occupancyId) return;
    setActionBusy('resend');
    setSendError(null);
    try {
      const res = await resendAnimalAddendumIntake(ctx.occupancyId);
      toast.success(
        `Animal form sent again — ${res.recipientsEmailed} ${res.recipientsEmailed === 1 ? 'link' : 'links'} emailed.`,
        { title: 'Animal addendum' },
      );
      await reload();
    } catch (err) {
      setSendError(animalAddendumErrorMessage(err, 'Could not resend the animal form'));
    } finally {
      setActionBusy(null);
    }
  };

  // --- Sent: the form is out; the page is done. ---
  if (sent) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div style={{ marginBottom: 'var(--space-7)' }}>
          <h1
            style={{
              fontSize: 'var(--text-xl)',
              fontWeight: 'var(--weight-bold)',
              color: 'var(--fg)',
              margin: 0,
            }}
          >
            Animal addendum
          </h1>
        </div>
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Form sent</h3>
          </div>
          <div className="stack-sm">
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              {sent.names.length > 0
                ? `${sent.names.join(', ')} ${sent.names.length === 1 ? 'has' : 'have'} been emailed the animal form.`
                : 'The animal form has been emailed to the household.'}{' '}
              The first person to complete it sends the addendum out for signature — everyone signs,
              then WHP countersigns. Nothing else is needed from you.
            </p>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Track it on the <Link to="/requests">Requests page</Link>.
            </p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <div style={{ marginBottom: 'var(--space-7)' }}>
        <h1
          style={{
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--weight-bold)',
            color: 'var(--fg)',
            margin: 0,
          }}
        >
          Animal addendum
        </h1>
        <p
          style={{
            fontSize: 'var(--text-sm)',
            color: 'var(--fg-muted)',
            marginTop: 'var(--space-2)',
          }}
        >
          A current tenant is adding an animal. Confirm the rent and deposit below, and the
          household gets a short form for the animal&apos;s details and a photo. The first person to
          complete it sends the addendum out for signature automatically — there is no review step
          afterwards, so check the money now.
        </p>
      </div>

      {ctx && notEnabled && (
        <div className="alert" data-tone="info" role="note" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">
            <div className="alert-title">Sends are turned off</div>
            Animal addendum sends are off right now (<code>ANIMAL_ADDENDUM_SEND_ENABLED</code>), so
            this page is read-only. Everything below is what WOULD go out.
          </div>
        </div>
      )}

      <div className="stack">
        {/* Property + unit */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Property &amp; unit</h3>
          </div>
          <div className="stack-sm">
            {propsError && (
              <div className="alert" data-tone="danger" role="alert">
                <div className="alert-body">{propsError}</div>
              </div>
            )}
            <div className="field">
              <label htmlFor="aap-prop">Property</label>
              <SearchSelect
                id="aap-prop"
                options={properties.map((p) => ({
                  value: p.propertyId,
                  label: p.name,
                  sublabel: p.address,
                }))}
                value={propertyId}
                onChange={setPropertyId}
                disabled={propsLoading}
                placeholder={propsLoading ? 'Loading…' : 'Select or search by name or address…'}
                emptyText="No matching properties"
              />
            </div>
            <div className="field">
              <label htmlFor="aap-unit">Unit</label>
              <select
                id="aap-unit"
                className="input"
                value={unitId}
                onChange={(e) => setUnitId(e.target.value)}
                disabled={!propertyId || unitsLoading}
              >
                <option value="">
                  {!propertyId
                    ? 'Select a property first…'
                    : unitsLoading
                      ? 'Loading units…'
                      : units.length === 0
                        ? 'No units found for this property'
                        : 'Select a unit…'}
                </option>
                {units.map((u) => (
                  <option key={u.unitId} value={u.unitId}>
                    {unitOptionLabel(u)}
                    {u.address ? ` — ${u.address}` : ''}
                  </option>
                ))}
              </select>
              {unitsError && (
                <span className="helper" data-state="error">
                  {unitsError}
                </span>
              )}
              {ctxError && (
                <span className="helper" data-state="error">
                  {ctxError}
                </span>
              )}
              {ctxLoading && <span className="helper">Loading the tenancy…</span>}
            </div>
          </div>
        </section>

        {/* Prior round, when there is one. */}
        {ctx && prior && (
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Already sent</h3>
            </div>
            <div className="stack-sm">
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                <span className="pill" data-tone={prior.tone}>
                  {prior.headline}
                </span>
                {ctx.priorSend?.initiatedAt && (
                  <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
                    Form sent {fmtDate(ctx.priorSend.initiatedAt)}
                    {ctx.priorSend.agreementSentAt
                      ? ` · Addendum sent ${fmtDate(ctx.priorSend.agreementSentAt)}`
                      : ''}
                  </span>
                )}
              </div>
              {prior.open && (
                <>
                  <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                    A new send is refused while this one is live. Cancel it to close the links and
                    tell the household there is nothing further to do, or resend the form to start
                    their answers over (this voids the agreement if one is out for signing).
                  </p>
                  {/* This card is a SNAPSHOT from when the unit was picked, and the
                      household may have finished signing since. Without a way to
                      re-read it, an operator looking at a stale "out for signature"
                      has only Cancel and Resend in front of them — and cancelling an
                      addendum that was signed a minute ago is the worst thing this
                      page could help somebody do. Checking again is the cheap out. */}
                  <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: 0 }}>
                    Showing what was true when you picked the unit. If the household has signed
                    since, check again rather than cancelling.
                  </p>
                  <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      className="btn"
                      data-variant="secondary"
                      data-size="sm"
                      disabled={actionBusy !== null}
                      onClick={() => {
                        setActionBusy('refresh');
                        void reload().finally(() => setActionBusy(null));
                      }}
                    >
                      {actionBusy === 'refresh' ? 'Checking…' : 'Check again'}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      data-variant="secondary"
                      data-size="sm"
                      disabled={actionBusy !== null || notEnabled}
                      onClick={() => void handleCancel()}
                    >
                      {actionBusy === 'cancel' ? 'Cancelling…' : 'Cancel'}
                    </button>
                    <button
                      type="button"
                      className="btn"
                      data-variant="secondary"
                      data-size="sm"
                      disabled={actionBusy !== null || notEnabled}
                      onClick={() => void handleResend()}
                    >
                      {actionBusy === 'resend' ? 'Resending…' : 'Resend form'}
                    </button>
                  </div>
                </>
              )}
              {prior.executed && (
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                  That addendum is signed and filed. Sending again starts a fresh one — use it when
                  the household is adding another animal.
                </p>
              )}
              {!prior.open && !prior.executed && (
                // Finished without being signed — declined, or cancelled. There is
                // nothing left to cancel or resend and nothing to duplicate, so the
                // card says only what is true and leaves Send plainly available.
                <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                  Nothing is outstanding from that round, so a new one can go out whenever you
                  are ready.
                </p>
              )}
            </div>
          </section>
        )}

        {/* Who gets the form */}
        <section className="card">
          <div className="card-header">
            <h3 className="card-title">Who gets the form</h3>
          </div>
          {!ctx ? (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
              Pick a property and unit to load the household.
            </p>
          ) : (
            <div className="stack-sm">
              {recipients.length === 0 && (
                <div className="alert" data-tone="warning" role="note">
                  <div className="alert-body">
                    AppFolio shows no current residents on this unit. An animal addendum needs an
                    existing tenancy — add the signers below only if you know who they are.
                  </div>
                </div>
              )}
              <p
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--fg-muted)',
                  margin: '0 0 var(--space-2)',
                }}
              >
                Pre-filled from AppFolio (editable). Everyone here gets the form, and everyone here
                signs the addendum. Any one of them can fill it in — the first answers we get are the
                ones we use.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {recipients.map((r, idx) => {
                  const emailInvalid = r.email.trim().length > 0 && !EMAIL_RE.test(r.email.trim());
                  const emailEmpty = !r.email.trim();
                  return (
                    <div
                      key={r.key}
                      style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}
                    >
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <input
                          type="text"
                          className="input"
                          placeholder={`Signer ${idx + 1} first name`}
                          value={r.firstName}
                          onChange={(e) => updateRecipient(r.key, { firstName: e.target.value })}
                          aria-label={`Signer ${idx + 1} first name`}
                        />
                        <span className="pill" data-tone="neutral" style={{ alignSelf: 'flex-start' }}>
                          {r.kind === 'cosigner' ? 'Co-signer' : 'Resident'}
                        </span>
                      </div>
                      <input
                        type="text"
                        className="input"
                        style={{ flex: 1 }}
                        placeholder={`Signer ${idx + 1} last name`}
                        value={r.lastName}
                        onChange={(e) => updateRecipient(r.key, { lastName: e.target.value })}
                        aria-label={`Signer ${idx + 1} last name`}
                      />
                      <div style={{ flex: 1.4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <input
                          type="email"
                          className="input"
                          placeholder="resident@example.com"
                          value={r.email}
                          onChange={(e) => updateRecipient(r.key, { email: e.target.value })}
                          aria-label={`Signer ${idx + 1} email`}
                          aria-invalid={emailInvalid}
                        />
                        {emailInvalid && (
                          <span className="helper" data-state="error">
                            Enter a valid email.
                          </span>
                        )}
                        {emailEmpty && (
                          <span className="helper" data-state="error">
                            Email required to send.
                          </span>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn"
                        data-variant="ghost"
                        data-size="sm"
                        data-icon-only
                        aria-label={`Remove signer ${idx + 1}`}
                        onClick={() => removeRecipient(r.key)}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="btn"
                  data-variant="secondary"
                  data-size="sm"
                  onClick={() => setRecipients((prev) => [...prev, blankRow()])}
                  style={{ alignSelf: 'flex-start' }}
                >
                  + Add signer
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Animals already on the tenancy */}
        {ctx && (
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Animals already on this tenancy</h3>
            </div>
            {ctx.currentAnimals.length === 0 ? (
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                None on file.
              </p>
            ) : (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 'var(--space-5)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--fg-muted)',
                }}
              >
                {ctx.currentAnimals.map((a, i) => (
                  <li key={`${a.name}-${i}`}>
                    {[a.name, a.typeLabel, a.age ? `${a.age} years old` : '', a.weightLbs ? `${a.weightLbs} lb` : '']
                      .filter(Boolean)
                      .join(' · ')}
                  </li>
                ))}
              </ul>
            )}
            <p
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--fg-muted)',
                margin: 'var(--space-3) 0 0',
              }}
            >
              The household sees these read-only on the form, so they only enter what&apos;s new.
              The addendum lists all of them.
            </p>
          </section>
        )}

        {/* The money */}
        {ctx && (
          <section className="card">
            <div className="card-header">
              <h3 className="card-title">Animal rent &amp; deposit</h3>
            </div>
            <div className="stack-sm">
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--fg-muted)', margin: 0 }}>
                Confirm what is charged today and what this addendum adds. There is no review step
                after the household submits — these numbers go straight onto the document.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
                <div className="field">
                  <label htmlFor="aap-cur-rent">Animal rent currently charged (per month)</label>
                  <MoneyInput
                    id="aap-cur-rent"
                    valueCents={currentRentCents}
                    onChangeCents={setCurrentRentCents}
                    placeholder="0"
                  />
                  <span className="helper">{chargeHelper('rent')}</span>
                </div>
                <div className="field">
                  <label htmlFor="aap-cur-dep">Animal deposit currently held</label>
                  <MoneyInput
                    id="aap-cur-dep"
                    valueCents={currentDepositCents}
                    onChangeCents={setCurrentDepositCents}
                    placeholder="0"
                  />
                  <span className="helper">{chargeHelper('deposit')}</span>
                </div>
                <div className="field">
                  <label htmlFor="aap-new-rent">Rent per new animal (per month)</label>
                  <MoneyInput
                    id="aap-new-rent"
                    valueCents={rentPerAnimalCents}
                    onChangeCents={setRentPerAnimalCents}
                  />
                  <span
                    className="helper"
                    data-state={rentPerAnimalCents <= 0 ? 'error' : undefined}
                  >
                    {rentPerAnimalCents <= 0
                      ? 'Rent per new animal has to be more than $0.'
                      : hasAccommodation
                        ? 'Charged for each other animal the household adds — not the RRA or pending RRA animals below.'
                        : 'Charged for each animal the household adds.'}
                  </span>
                </div>
                <div className="field">
                  <label htmlFor="aap-new-dep">Deposit per new animal</label>
                  <MoneyInput
                    id="aap-new-dep"
                    valueCents={depositPerAnimalCents}
                    onChangeCents={setDepositPerAnimalCents}
                  />
                  <span className="helper">Due when the addendum is signed.</span>
                </div>
                <div className="field">
                  <label htmlFor="aap-start">Animal rent starts on</label>
                  <input
                    id="aap-start"
                    type="date"
                    className="input"
                    value={rentStartDate}
                    onChange={(e) => setRentStartDate(e.target.value)}
                    aria-invalid={!rentStartDate}
                  />
                  <span className="helper" data-state={!rentStartDate ? 'error' : undefined}>
                    Defaults to the 1st of next month — the lease has no proration for animals.
                  </span>
                </div>
              </div>
              {/* WEL-182: roster-only. Animals marked here are $0 on the household's form and the addendum. */}
              {mayMarkAccommodation ? (
                <AccommodationAnimalsBlock
                  rows={accommodationRows}
                  rates={accommodationRates}
                  onAdd={addAccommodation}
                  onUpdate={updateAccommodation}
                  onRemove={removeAccommodation}
                />
              ) : null}
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)', margin: 0 }}>
                {hasAccommodation ? 'Any other animal' : 'Per new animal'}: {formatMoneyCents(rentPerAnimalCents)}/month
                {rentStartDate ? ` from ${fmtDate(rentStartDate)}` : ''} and a{' '}
                {formatMoneyCents(depositPerAnimalCents)} deposit
                {currentRentCents > 0 || currentDepositCents > 0 ? (
                  <>
                    , on top of the {formatMoneyCents(currentRentCents)}/month and{' '}
                    {formatMoneyCents(currentDepositCents)} deposit already in place
                  </>
                ) : null}
                . The addendum states the totals once we know how many animals there are.
              </p>
              {accommodationSummary.length > 0 ? (
                <ul
                  data-section="accommodation-summary"
                  style={{
                    margin: 0,
                    paddingLeft: 'var(--space-5)',
                    fontSize: 'var(--text-sm)',
                    color: 'var(--fg-muted)',
                  }}
                >
                  {accommodationSummary.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </section>
        )}

        {/* Action */}
        <section className="card">
          <div className="stack-sm">
            {sendError && (
              <div className="alert" data-tone="danger" role="alert">
                <div className="alert-body">{sendError}</div>
              </div>
            )}
            {ctx && reasons.length > 0 && (
              <ul
                style={{
                  margin: 0,
                  paddingLeft: 'var(--space-5)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--fg-muted)',
                }}
              >
                {reasons.map((reason, i) => (
                  <li key={i}>{reason}</li>
                ))}
              </ul>
            )}
            <button
              type="button"
              className="btn"
              onClick={handleSendClick}
              disabled={!canSend}
              style={{ alignSelf: 'flex-start', marginTop: 'var(--space-2)' }}
            >
              {busy
                ? 'Sending…'
                : prior?.executed
                  ? 'Send another animal form →'
                  : `Send animal form${included.length > 0 && reasons.length === 0 ? ` to ${included.length}` : ''} →`}
            </button>
          </div>
        </section>
      </div>

      {/* The server stops refusing once a prior addendum is finished, so this is
          the last guard. It names the date of the one already on file, because
          "you already have one" is not actionable — "from March 4" is what lets
          somebody realise they are looking at the wrong tenancy. */}
      <ConfirmDialog
        open={confirmSecond}
        onOpenChange={setConfirmSecond}
        title="Send another animal addendum?"
        description={
          prior?.executedAt
            ? `This tenancy already has an executed animal addendum from ${fmtDate(prior.executedAt)}. Sending another starts a fresh one — use it when the household is adding another animal.`
            : 'This tenancy already has an executed animal addendum. Sending another starts a fresh one — use it when the household is adding another animal.'
        }
        confirmLabel="Send another"
        tone="primary"
        busy={busy}
        busyLabel="Sending…"
        onConfirm={() => void handleSend()}
      />
    </div>
  );
}
